import React, { useState } from "react";
import { KeyRound, Loader2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Modal, FieldLabel, inputCls, InlineMsg } from "./shared";
import { saveService } from "../../lib/integrationsApi";

export default function EditKeyModal({ service, actor, onClose, onSaved }) {
  const [values, setValues] = useState({});
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const hasInput = Object.values(values).some(v => v.trim());

  async function submit(e) {
    e.preventDefault();
    if (!hasInput || busy) return;
    setBusy(true); setError(null);
    try {
      await saveService(service.service, values, actor);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`${service.configured ? "Update" : "Add"} ${service.label} credentials`}
           subtitle="Encrypted at rest · leave a field blank to keep its current value"
           icon={KeyRound} onClose={onClose} busy={busy} testid="key-edit-modal">
      <form onSubmit={submit} className="p-5 space-y-3.5">
        {service.docs && (
          <a href={service.docs} target="_blank" rel="noopener noreferrer" data-testid="key-edit-docs"
             className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
            Where do I get this? <ExternalLink size={10} />
          </a>
        )}
        {service.fields.map(f => (
          <FieldLabel key={f.name} label={f.label} hint={f.configured ? `current: ${f.masked}` : "not set"}>
            <div className="relative">
              <input
                data-testid={`key-input-${f.name}`}
                type={f.secret && !reveal[f.name] ? "password" : "text"}
                value={values[f.name] || ""}
                onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                placeholder={f.placeholder || ""}
                autoComplete="new-password" spellCheck="false" disabled={busy}
                className={`${inputCls} ${f.secret ? "pr-9" : ""}`}
              />
              {f.secret && (
                <button type="button" data-testid={`key-reveal-${f.name}`}
                        onClick={() => setReveal(r => ({ ...r, [f.name]: !r[f.name] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-muted hover:text-fg">
                  {reveal[f.name] ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              )}
            </div>
          </FieldLabel>
        ))}
        {service.category === "mapping" && (
          <div className="text-[10.5px] text-muted bg-surface-2/60 border border-border rounded-sm p-2.5 leading-relaxed">
            Map keys are browser keys: tiles load directly from the provider, so restrict the key to this app's domain in the provider console.
          </div>
        )}
        {error && <InlineMsg testid="key-edit-error">{error}</InlineMsg>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy} data-testid="key-edit-cancel"
                  className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2 disabled:opacity-40">
            Cancel
          </button>
          <button type="submit" disabled={!hasInput || busy} data-testid="key-edit-save"
                  className="px-4 py-1.5 text-[11.5px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
            {busy ? "Saving…" : "Save securely"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
